import { useMemo, useState } from "react";
import Dayjs, { type Dayjs as DayjsType } from "dayjs";
import { Button, Typography } from "antd";

const { Text } = Typography;

const WEEK_LABELS = ["一", "二", "三", "四", "五", "六", "日"];

function toDateKey(value: DayjsType) {
  return value.format("YYYY-MM-DD");
}

export function defaultRecentExportDateKeys(count = 5): string[] {
  const keys: string[] = [];
  const today = Dayjs().startOf("day");
  for (let i = count - 1; i >= 0; i -= 1) {
    keys.push(toDateKey(today.subtract(i, "day")));
  }
  return keys;
}

export function DateMultiPicker({
  value,
  onChange,
  maxDays = 31,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  maxDays?: number;
}) {
  const [cursor, setCursor] = useState(() => Dayjs().startOf("month"));
  const selected = useMemo(() => new Set(value), [value]);
  const today = Dayjs().startOf("day");

  const cells = useMemo(() => {
    const start = cursor.startOf("month");
    // Monday-first grid: day() 0=Sun … 6=Sat
    const offset = (start.day() + 6) % 7;
    const gridStart = start.subtract(offset, "day");
    return Array.from({ length: 42 }, (_, index) => gridStart.add(index, "day"));
  }, [cursor]);

  const summary = useMemo(() => {
    if (value.length === 0) return "未选择日期";
    const sorted = [...value].sort();
    const label = sorted
      .map((key) => {
        const d = Dayjs(key);
        return `${d.month() + 1}/${d.date()}`;
      })
      .join("、");
    return `已选 ${value.length} 天：${label}`;
  }, [value]);

  const toggle = (day: DayjsType) => {
    if (day.isAfter(today, "day")) return;
    const key = toDateKey(day);
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else {
      if (next.size >= maxDays) return;
      next.add(key);
    }
    onChange([...next].sort());
  };

  return (
    <div className="group-chat-mini-cal">
      <div className="group-chat-mini-cal-nav">
        <button
          type="button"
          className="group-chat-mini-cal-nav-btn"
          onClick={() => setCursor((prev) => prev.subtract(1, "month"))}
          aria-label="上个月"
        >
          ‹
        </button>
        <span className="group-chat-mini-cal-title">{cursor.format("YYYY年M月")}</span>
        <button
          type="button"
          className="group-chat-mini-cal-nav-btn"
          onClick={() => setCursor((prev) => prev.add(1, "month"))}
          aria-label="下个月"
        >
          ›
        </button>
      </div>
      <div className="group-chat-mini-cal-week">
        {WEEK_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="group-chat-mini-cal-grid">
        {cells.map((day) => {
          const key = toDateKey(day);
          const inMonth = day.month() === cursor.month();
          const isFuture = day.isAfter(today, "day");
          const isSelected = selected.has(key);
          const isToday = day.isSame(today, "day");
          const classNames = [
            "group-chat-mini-cal-day",
            inMonth ? "" : "is-outside",
            isSelected ? "is-selected" : "",
            isToday ? "is-today" : "",
            isFuture ? "is-disabled" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={key + (inMonth ? "" : "-o")}
              type="button"
              className={classNames}
              disabled={isFuture}
              onClick={() => toggle(day)}
            >
              {day.date()}
            </button>
          );
        })}
      </div>
      <div className="group-chat-mini-cal-actions">
        <Button
          type="link"
          size="small"
          onClick={() => onChange(defaultRecentExportDateKeys(5))}
        >
          最近 5 天
        </Button>
        <Button type="link" size="small" onClick={() => onChange([])}>
          清空
        </Button>
      </div>
      <Text type="secondary" className="group-chat-mini-cal-summary" title={summary}>
        {summary}
      </Text>
    </div>
  );
}
